import { t } from '@ctcells/i18n'
import { useNavigate, useParams } from 'react-router'

import { BatchDetailContainer } from '../screens/batches/BatchDetailContainer.tsx'
import { BatchesScreen } from '../screens/batches/BatchesScreen.tsx'
import { useRunningDeviceKey } from '../screens/monitoring/followRunningDevice.ts'
import { MonitoringScreen } from '../screens/monitoring/MonitoringScreen.tsx'
import { monitoringDeviceKey } from '../screens/monitoring/monitoringModel.ts'
import { TABLET_HOME_PATH, TABLET_PATH } from '../shell/tabletNav.ts'

import { DEMO_ACCESS } from './demoAccess.ts'

/**
 * 태블릿 kiosk 라우트가 꽂는 **얇은 어댑터 컴포넌트**들.
 *
 * 🔴 **화면을 새로 만들지 않았다.** 콘솔 화면 3종을 그대로 싣고 **이동 규칙만 태블릿 것**으로 준다.
 *    화면 컴포넌트는 라우터를 모르기 때문에(`onXxx` 어댑터) 복제가 필요 없다.
 *
 * 표(`tabletRoutes.tsx`)와 파일을 나눈 이유는 콘솔 쪽과 같다 — 컴포넌트만 내보내는 파일이어야
 * 개발 서버 핫리로드가 성립한다.
 *
 * ⚠ 권한은 **`DEMO_ACCESS`(자리표시자)** 다. 콘솔 라우트 조각들과 같은 사정이다.
 */

/**
 * 태블릿 안에서만 쓰는 경로. 내비 목록이 유일한 출처다.
 *
 * ⚠ **셸(`TabletLayout`)의 상시 작업 줄도 같은 표를 본다** — 표를 여기 또 만들면 작업 줄과
 *   화면이 서로 다른 곳으로 간다(2026-09-21 통합).
 */
const PATH = TABLET_PATH

/**
 * ⚠ 태블릿은 아직 **화면을 직접** 꽂는다(권한도 자리표시자라 컨테이너를 안 거친다).
 *    그래서 「지금 도는 모듈」도 여기서 읽는다 — 콘솔은 컨테이너가 같은 훅을 부른다.
 *    태블릿이 컨테이너로 옮겨 가면 이 두 줄은 사라진다.
 */
export function TabletMonitoringRoute() {
  const navigate = useNavigate()
  const params = useParams()
  const deviceKey = monitoringDeviceKey(params['moduleKey'])
  const runningDevice = useRunningDeviceKey()
  const to = (key: string) => `${PATH['monitoring'] ?? ''}/${key}`

  return (
    <MonitoringScreen
      {...(deviceKey === undefined ? {} : { deviceKey })}
      {...(runningDevice === undefined ? {} : { runningDevice })}
      access={DEMO_ACCESS}
      /* 🔴 자동 전환은 이력을 쌓지 않는다(콘솔과 같은 규율) */
      onFollowDevice={(key) => void navigate(to(key), { replace: true })}
      onSelectDevice={(key) => void navigate(to(key))}
    />
  )
}

export function TabletBatchesRoute() {
  const navigate = useNavigate()
  // 🔴 새 작업은 태블릿에 싣지 않는다(데스크톱 몫). 목록에서 상세로만 간다.
  return (
    <BatchesScreen
      access={DEMO_ACCESS}
      onOpenBatch={(batchId) => void navigate(`${PATH['batches'] ?? ''}/${batchId}`)}
    />
  )
}

/** 🔴 콘솔과 **같은 컨테이너**를 쓴다 — 태블릿 전용 트리를 만들지 않는다 */
export function TabletBatchDetailRoute() {
  const navigate = useNavigate()
  return (
    <BatchDetailContainer
      backLabel={t('screen.batches.title')}
      onBack={() => void navigate(PATH['batches'] ?? TABLET_HOME_PATH)}
    />
  )
}
